import { InteractionArgs, LmsAdapter } from './types';

/**
 * Fans every call out to all wrapped adapters. Per product requirement:
 * if multiple LMS hosts are detected (e.g. RXD wrapper around a SCORM
 * package) we want both to receive every event in parallel rather than
 * choose one as the "primary".
 *
 * Every individual call is wrapped so a throwing adapter can never break
 * the others.
 */
export function createCompositeAdapter(adapters: LmsAdapter[]): LmsAdapter {
  const fanOut = (method: keyof LmsAdapter, args: InteractionArgs): void => {
    for (const adapter of adapters) {
      try {
        const fn = adapter[method] as (...a: unknown[]) => void;
        fn.apply(adapter, [...args] as unknown[]);
      } catch (error) {
        console.error(
          `[lms/composite] adapter "${adapter.name}" failed on ${String(
            method
          )}:`,
          error
        );
      }
    }
  };

  return {
    get name(): string {
      // Re-read child names lazily — `scormAgainAdapter` reports
      // `scorm-again:pending` until the bridge is actually instantiated, so
      // freezing the composite name at construction time would mask that
      // transition in diagnostics.
      return `composite[${adapters.map(a => a.name).join(',') || 'empty'}]`;
    },
    get isAvailable(): boolean {
      // Each child adapter does per-call host detection (see `rxdAdapter` /
      // `scormAgainAdapter`). Mirror that here so consumers see the
      // up-to-date detection result instead of whatever was true at boot.
      return adapters.some(a => a.isAvailable);
    },

    setProgressMeasure: value => fanOut('setProgressMeasure', [value]),
    setScore: (score, maxScore, minScore) =>
      fanOut('setScore', [score, maxScore, minScore]),
    setReachedEnd: () => fanOut('setReachedEnd', []),
    setPassed: () => fanOut('setPassed', []),
    concedeControl: () => fanOut('concedeControl', []),

    recordMultipleChoiceInteraction: (...args) =>
      fanOut('recordMultipleChoiceInteraction', args),
    recordTrueFalseInteraction: (...args) =>
      fanOut('recordTrueFalseInteraction', args),
    recordSequencingInteraction: (...args) =>
      fanOut('recordSequencingInteraction', args),
    recordFillInInteraction: (...args) =>
      fanOut('recordFillInInteraction', args),

    // Read APIs need different fan-out semantics than write APIs: we want
    // one definitive answer, not parallel side effects. Query each adapter
    // in parallel (so the slowest one doesn't dominate latency) and return
    // the first non-empty preference. RXD currently always returns `''`
    // here, so in the typical "RXD + scorm-again" composition the answer
    // comes from scorm-again whenever the LMS provided one.
    getLearnerLanguagePreference: async () => {
      const results = await Promise.all(
        adapters.map(async adapter => {
          try {
            const value = (await adapter.getLearnerLanguagePreference()) || '';
            return value;
          } catch (error) {
            console.error(
              `[lms/composite] adapter "${adapter.name}" failed on getLearnerLanguagePreference:`,
              error
            );
            return '';
          }
        })
      );
      const winner = results.find(value => value && value.length > 0) ?? '';
      return winner;
    }
  };
}
